import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

// Which products require an address form after payment?
// - programme_coffret (152€) → coffret physique
// - correspondance_simple (35€) → renvoi de la lettre manuscrite
// - correspondance_coffret (59€) → lettre + coffret
// - abonnement_correspondance (27€/mo) → correspondance mensuelle
// Others (programme_seul, gift_card, atelier) → pas besoin d'adresse
const ADDRESS_CONFIG = {
  programme_coffret:         { kind: "coffret",        label: "Adresse pour recevoir le coffret",              cta: "Enregistrer mon adresse", needConsent: false },
  correspondance_simple:     { kind: "correspondance", label: "Adresse pour recevoir la réponse manuscrite",   cta: "Enregistrer mon adresse", needConsent: true  },
  correspondance_coffret:    { kind: "correspondance", label: "Adresse pour la lettre et le coffret",          cta: "Enregistrer mon adresse", needConsent: true  },
  abonnement_correspondance: { kind: "correspondance", label: "Adresse pour la correspondance mensuelle",      cta: "Enregistrer mon adresse", needConsent: true  },
};

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const free = sp.get("free");
  const [status, setStatus] = useState(free ? "paid" : "pending");
  const [lookupKey, setLookupKey] = useState(null);
  const [addr, setAddr] = useState({full_name:"", line1:"", line2:"", city:"", postal_code:"", country:"France"});
  const [consent, setConsent] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (free) return;
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setStatus(r.data.payment_status);
        setLookupKey(r.data.lookup_key || null);
        if (r.data.payment_status === "paid") return;
      } catch (e) { console.warn("Statut paiement indisponible, nouvel essai…", e?.message); }
      if (attempts++ < 12) setTimeout(poll, 2500);
    };
    poll();
  }, [sessionId, free]);

  const config = lookupKey ? ADDRESS_CONFIG[lookupKey] : null;

  async function submitAddress() {
    if (!config) return;
    if (config.needConsent && !consent) return toast.error("Consentement requis");
    try {
      const url = config.kind === "correspondance" ? "/correspondance/form" : "/orders/address";
      await api.post(url, { session_id: sessionId, ...addr, ...(config.needConsent ? {consent} : {}) });
      setSaved(true);
      toast.success("Merci — c'est enregistré.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  }

  return (
    <div data-testid="payment-success" className="max-w-2xl mx-auto px-6 md:px-12 py-20 text-center">
      {status === "paid" ? (
        <>
          <CheckCircle2 size={56} className="mx-auto text-[#7B8A72] mb-6"/>
          <h1 className="font-serif text-5xl mb-4">Merci</h1>
          <p className="font-hand text-2xl text-[#A3876A] mb-8">Ta commande est confirmée</p>

          {!saved && sessionId && config && (
            <div className="text-left bg-[#F5F0E6] p-8 border border-[#E8DCC8] mt-10">
              <h2 className="font-serif text-2xl mb-2">{config.label}</h2>
              <p className="text-sm text-[#7C7064] mb-6">Merci de compléter tes coordonnées ci-dessous.</p>
              <div className="space-y-4">
                <input data-testid="addr-full-name" className="field-input" placeholder="Nom complet" value={addr.full_name} onChange={e=>setAddr({...addr,full_name:e.target.value})}/>
                <input data-testid="addr-line1" className="field-input" placeholder="Adresse" value={addr.line1} onChange={e=>setAddr({...addr,line1:e.target.value})}/>
                <input data-testid="addr-line2" className="field-input" placeholder="Complément (facultatif)" value={addr.line2} onChange={e=>setAddr({...addr,line2:e.target.value})}/>
                <div className="grid grid-cols-2 gap-4">
                  <input data-testid="addr-city" className="field-input" placeholder="Ville" value={addr.city} onChange={e=>setAddr({...addr,city:e.target.value})}/>
                  <input data-testid="addr-postal" className="field-input" placeholder="Code postal" value={addr.postal_code} onChange={e=>setAddr({...addr,postal_code:e.target.value})}/>
                </div>
                <input data-testid="addr-country" className="field-input" placeholder="Pays" value={addr.country} onChange={e=>setAddr({...addr,country:e.target.value})}/>
                {config.needConsent && (
                  <label className="flex items-start gap-2 text-xs text-[#7C7064]">
                    <input data-testid="addr-consent" type="checkbox" checked={consent} onChange={e=>setConsent(e.target.checked)} className="mt-1"/>
                    <span>J'accepte que mon adresse soit utilisée pour recevoir la correspondance{lookupKey==="correspondance_coffret" ? " et le coffret" : ""}.</span>
                  </label>
                )}
                <div className="pt-2">
                  <button data-testid="save-address" onClick={submitAddress} className="btn-primary w-full justify-center">
                    {config.cta}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!saved && sessionId && !config && lookupKey && (
            <p className="text-[#4A4036] mt-6">Tu vas recevoir un email de confirmation dans quelques instants.</p>
          )}

          {saved && <p className="text-[#4A4036] mt-6">Tes coordonnées sont enregistrées. Tu vas recevoir un email de confirmation.</p>}
          <div className="mt-10"><Link to="/" className="btn-outline">Retour à l'accueil</Link></div>
        </>
      ) : (
        <>
          <h1 className="font-serif text-4xl mb-4">Confirmation en cours…</h1>
          <p className="text-[#4A4036]">Merci de patienter quelques instants.</p>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel" className="max-w-xl mx-auto px-6 md:px-12 py-20 text-center">
      <XCircle size={56} className="mx-auto text-[#A86962] mb-6"/>
      <h1 className="font-serif text-4xl mb-4">Paiement annulé</h1>
      <p className="text-[#4A4036] mb-8">Aucun débit n'a été effectué. Tu peux revenir en arrière ou m'écrire si tu as une question.</p>
      <Link to="/" className="btn-primary">Retour à l'accueil</Link>
    </div>
  );
}
